/* Planilha personalizada: descobre qual coluna corresponde a cada campo da categoria,
   olhando o nome do cabeçalho e o conteúdo das células (datas, números, valores que
   batem com as listas do catálogo). Funções puras, usadas pela tela e pelos testes. */

import { isoDe, isoDeTexto, parseNum } from './formato';
import { casarReferencia, palavrasRef, textoRef, type ItemReferencia } from './referencias';
import type { Campo, Catalogo, Categoria } from '../tipos/api';

export interface LinhaPlanilha {
  l: unknown[];
  /** Número da linha no Excel. */
  n: number;
}

export interface Planilha {
  arquivo: string;
  aba: string;
  linhaCab: number;
  /** Cabeçalho completo, na posição de cada coluna (pode ter vazios). */
  cabecalho: string[];
  rows: LinhaPlanilha[];
}

export interface Sugestao {
  coluna: string | null;
  /** 0..1 */
  confianca: number;
  motivo: string;
}

/** Outros nomes comuns de cada campo nas planilhas dos clientes. */
const SINONIMOS: Record<string, string[]> = {
  data: ['data', 'dt', 'data emissao', 'emissao', 'competencia', 'mes', 'periodo', 'dia', 'data da nota', 'data do servico'],
  descricao: ['descricao', 'historico', 'observacao', 'obs', 'item', 'detalhe', 'servico', 'documento', 'nota fiscal'],
  id_unidade: ['obra', 'unidade', 'empreendimento', 'centro de custo', 'filial', 'local', 'projeto', 'unidade de negocio'],
  cnpj_fornecedor: ['cnpj', 'cnpj fornecedor', 'fornecedor', 'emitente', 'transportadora', 'prestador'],
  link_referencia: ['link', 'url', 'anexo', 'arquivo'],
  consumo: ['consumo', 'quantidade', 'qtd', 'qtde', 'litros', 'volume'],
  consumo_mensal: ['consumo', 'quantidade', 'qtd', 'qtde', 'litros', 'volume', 'kwh', 'mwh'],
  quantidade: ['quantidade', 'qtd', 'qtde', 'peso', 'massa', 'toneladas', 'tonelada', 'kg', 'volume'],
  unidade: ['unidade', 'un', 'und', 'unid', 'um', 'medida', 'unidade de medida'],
  id_combustivel: ['combustivel', 'tipo de combustivel', 'produto'],
  id_veiculo: ['veiculo', 'tipo de veiculo', 'frota'],
  ano_veiculo: ['ano', 'ano do veiculo', 'ano da frota', 'ano modelo'],
  id_setor: ['setor'],
  id_gas: ['gas', 'fluido', 'refrigerante', 'gas refrigerante'],
  existentes_recarga: ['recarga', 'quantidade', 'qtd', 'kg'],
  id_material: ['material', 'produto', 'insumo', 'item', 'descricao do material'],
  valor_total: ['valor', 'valor total', 'total', 'preco', 'montante', 'r'],
  id_residuo: ['residuo', 'tipo de residuo', 'descricao do residuo', 'material', 'classe'],
  id_destino: ['destino', 'destinacao', 'destinacao final', 'disposicao', 'disposicao final', 'tratamento final'],
  id_tratamento: ['tratamento', 'tipo de tratamento', 'sistema de tratamento'],
  volume_m3: ['volume', 'm3', 'vazao', 'esgoto', 'efluente', 'volume de esgoto'],
  dbo_kg_m3: ['dbo', 'carga organica', 'dbo5'],
  maximo_ch4: ['b0', 'capacidade maxima', 'maximo ch4'],
};

/** Unidades escritas de outros jeitos → opção da categoria. */
const UNIDADES: Record<string, string[]> = {
  t: ['t', 'ton', 'tons', 'tn', 'tonelada', 'toneladas'],
  kg: ['kg', 'kgs', 'quilo', 'quilos', 'quilograma', 'quilogramas'],
  'm³': ['m3', 'm 3', 'metro cubico', 'metros cubicos'],
  L: ['l', 'lt', 'lts', 'litro', 'litros'],
  m: ['m', 'metro', 'metros'],
  'm²': ['m2', 'm 2', 'metro quadrado', 'metros quadrados'],
};

const AMOSTRA = 60;

/** Opção (entre as da categoria) para uma unidade escrita de qualquer jeito. */
export function opcaoDeUnidade(valor: unknown, opcoes: string[]): string | null {
  const t = textoRef(valor);
  const direta = opcoes.find((o) => textoRef(o) === t);
  if (direta) return direta;
  const alvo = Object.entries(UNIDADES).find(([, nomes]) => nomes.includes(t))?.[0];
  return alvo && opcoes.includes(alvo) ? alvo : null;
}

export function itensDoCampo(campo: Campo, catalogo: Catalogo): ItemReferencia[] {
  if (campo.tipo === 'obra') return catalogo.obras;
  if (campo.tipo.startsWith('lista:')) return catalogo.listas[campo.tipo.slice(6)] || [];
  return [];
}

const preenchido = (v: unknown) => v !== null && v !== undefined && String(v).trim() !== '';

function ehData(v: unknown): boolean {
  if (v instanceof Date) return !isNaN(v.getTime());
  const t = String(v).trim();
  return /^\d{4}-\d{2}-\d{2}/.test(t) || /^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(t) || /^\d{1,2}\/\d{4}$/.test(t);
}

const ehNumero = (v: unknown) => Number.isFinite(parseNum(v));
const ehCnpj = (v: unknown) => /^\d{14}$/.test(String(v).replace(/\D/g, '')) || /^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}$/.test(String(v).trim());

/** 0..1: quanto o nome da coluna lembra o campo. */
export function pontuarCabecalho(campo: Campo, cabecalho: string): number {
  const h = textoRef(cabecalho);
  if (!h) return 0;
  const alvos = [campo.rotulo, campo.rotulo.replace(/\s*\(.*\)$/, ''), campo.nome.replace(/^id_/, '').replace(/_/g, ' '), ...(SINONIMOS[campo.nome] || [])]
    .map(textoRef).filter(Boolean);
  if (alvos.includes(h)) return 1;
  const ph = new Set(palavrasRef(h));
  let melhor = 0;
  for (const a of alvos) {
    const pa = palavrasRef(a);
    if (!pa.length) continue;
    const comuns = pa.filter((p) => ph.has(p)).length;
    if (comuns === pa.length) melhor = Math.max(melhor, 0.85 - 0.1 * Math.max(0, ph.size - pa.length));
    else if (comuns) melhor = Math.max(melhor, 0.4 * (comuns / pa.length));
  }
  return Math.max(0, melhor);
}

interface Conteudo {
  pontos: number;
  motivo: string;
  /** O conteúdo contradiz o tipo do campo (ex.: texto num campo numérico). */
  incompativel: boolean;
}

/** 0..1: quanto os valores da coluna servem para o campo. */
export function pontuarConteudo(campo: Campo, valores: unknown[], catalogo: Catalogo): Conteudo {
  const vs = valores.filter(preenchido).slice(0, AMOSTRA);
  if (!vs.length) return { pontos: 0, motivo: 'coluna vazia', incompativel: true };
  const fracao = (f: (v: unknown) => boolean) => vs.filter(f).length / vs.length;
  const de = (n: number) => `${Math.round(n * vs.length)} de ${vs.length}`;

  if (campo.tipo === 'data') {
    const f = fracao(ehData);
    return { pontos: f, motivo: `${de(f)} valores são datas`, incompativel: f < 0.5 };
  }
  if (campo.tipo === 'numero') {
    const f = fracao(ehNumero);
    return { pontos: 0.5 * f, motivo: `${de(f)} valores são números`, incompativel: f < 0.6 };
  }
  if (campo.tipo === 'opcoes') {
    const f = fracao((v) => opcaoDeUnidade(v, campo.opcoes) !== null);
    return { pontos: f, motivo: `${de(f)} valores são opções válidas (${campo.opcoes.join(', ')})`, incompativel: f < 0.5 };
  }
  if (campo.tipo === 'obra' || campo.tipo.startsWith('lista:')) {
    const itens = itensDoCampo(campo, catalogo);
    const distintos = [...new Set(vs.map((v) => String(v).trim()))];
    const ok = new Set(distintos.filter((v) => casarReferencia(v, itens).ok));
    const f = vs.filter((v) => ok.has(String(v).trim())).length / vs.length;
    const numericos = fracao((v) => ehNumero(v) && !itens.some((i) => String(i.id) === String(v).trim()));
    return { pontos: f, motivo: `${de(f)} valores identificados na lista`, incompativel: numericos > 0.5 };
  }
  if (campo.nome === 'cnpj_fornecedor') {
    const f = fracao(ehCnpj);
    return { pontos: f, motivo: `${de(f)} valores são CNPJ`, incompativel: f < 0.3 };
  }
  if (campo.nome === 'link_referencia') {
    const f = fracao((v) => /^https?:\/\//i.test(String(v).trim()));
    return { pontos: f, motivo: `${de(f)} valores são links`, incompativel: f < 0.3 };
  }
  // Texto livre (descrição): melhor quando é texto e varia de linha para linha.
  const texto = fracao((v) => !ehNumero(v) && !ehData(v));
  const variedade = new Set(vs.map((v) => String(v))).size / vs.length;
  return { pontos: 0.3 * texto + 0.2 * variedade, motivo: 'texto livre', incompativel: texto < 0.5 };
}

const PESO_CABECALHO: Record<string, number> = { numero: 0.75, texto: 0.7 };

function pontuar(campo: Campo, cabecalho: string, valores: unknown[], catalogo: Catalogo): Sugestao & { pontos: number } {
  const ph = pontuarCabecalho(campo, cabecalho);
  const pc = pontuarConteudo(campo, valores, catalogo);
  const peso = PESO_CABECALHO[campo.tipo] ?? 0.45;
  let pontos = peso * ph + (1 - peso) * pc.pontos;
  if (pc.incompativel) pontos *= 0.25;
  const motivos = [ph >= 0.85 ? 'nome da coluna' : ph > 0 ? 'nome parecido' : '', pc.pontos > 0 ? pc.motivo : ''].filter(Boolean);
  return { coluna: cabecalho, pontos, confianca: Math.min(1, pontos), motivo: motivos.join(' · ') || pc.motivo };
}

/** Valores da coluna (pela posição no cabeçalho). */
export const valoresDaColuna = (p: Planilha, coluna: string): unknown[] => {
  const i = p.cabecalho.indexOf(coluna);
  return i < 0 ? [] : p.rows.map((r) => r.l[i]);
};

const LIMIAR = 0.3;

/** Coluna sugerida para cada campo: as melhores combinações primeiro, cada coluna uma vez só. */
export function analisarColunas(c: Categoria, p: Planilha, catalogo: Catalogo): Record<string, Sugestao> {
  const colunas = p.cabecalho.filter(Boolean);
  const pares = c.campos.flatMap((campo) => colunas.map((col) => ({ campo, ...pontuar(campo, col, valoresDaColuna(p, col), catalogo) })));
  pares.sort((a, b) => b.pontos - a.pontos);
  const resultado: Record<string, Sugestao> = {};
  const usadas = new Set<string>();
  for (const par of pares) {
    if (par.pontos < LIMIAR || resultado[par.campo.nome] || usadas.has(par.coluna as string)) continue;
    resultado[par.campo.nome] = { coluna: par.coluna, confianca: par.confianca, motivo: par.motivo };
    usadas.add(par.coluna as string);
  }
  // Descrição sem coluna parecida: a coluna de texto livre que sobrou (nº do documento, observação…).
  const descricao = c.campos.find((x) => x.nome === 'descricao');
  if (descricao && !resultado.descricao) {
    const livre = colunas.filter((col) => !usadas.has(col))
      .map((col) => ({ col, conteudo: pontuarConteudo(descricao, valoresDaColuna(p, col), catalogo) }))
      .filter((x) => !x.conteudo.incompativel)
      .sort((a, b) => b.conteudo.pontos - a.conteudo.pontos)[0];
    if (livre) resultado.descricao = { coluna: livre.col, confianca: 0.35, motivo: 'coluna de texto livre que sobrou' };
  }
  c.campos.forEach((campo) => {
    if (!resultado[campo.nome]) resultado[campo.nome] = { coluna: null, confianca: 0, motivo: '' };
  });
  return resultado;
}

/** Quanto a planilha "parece" com cada categoria: média da confiança dos campos obrigatórios próprios dela
    (sem os comuns a todas e sem os que têm valor padrão). */
export function sugerirCategorias(p: Planilha, catalogo: Catalogo): { slug: string; nome: string; pontos: number }[] {
  return catalogo.categorias.map((c) => {
    const proprios = c.campos.filter((x) => x.obrigatorio && x.padrao === null && !['data', 'descricao', 'id_unidade'].includes(x.nome));
    const analise = analisarColunas(c, p, catalogo);
    const pontos = proprios.length ? proprios.reduce((s, x) => s + analise[x.nome].confianca, 0) / proprios.length : 0;
    return { slug: c.slug, nome: c.nome, pontos };
  }).sort((a, b) => b.pontos - a.pontos);
}

export type Leitura = { ok: true; valor: unknown; exibicao: string; aproximado?: boolean } | { ok: false; erro: string; sugestoes?: ItemReferencia[] };

/** Como o valor de uma célula vai ser lido no campo (para a pré-visualização). */
export function lerValor(campo: Campo, bruto: unknown, catalogo: Catalogo): Leitura {
  if (!preenchido(bruto)) {
    if (campo.padrao !== null && campo.padrao !== undefined) return { ok: true, valor: campo.padrao, exibicao: `${campo.padrao} (padrão)` };
    return campo.obrigatorio ? { ok: false, erro: 'vazio' } : { ok: true, valor: null, exibicao: '—' };
  }
  if (campo.tipo === 'data') {
    const iso = bruto instanceof Date ? isoDe(bruto) : isoDeTexto(bruto);
    return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? { ok: true, valor: iso, exibicao: iso.split('-').reverse().join('/') } : { ok: false, erro: 'data inválida' };
  }
  if (campo.tipo === 'numero') {
    const n = parseNum(bruto);
    return Number.isFinite(n) ? { ok: true, valor: n, exibicao: n.toLocaleString('pt-BR') } : { ok: false, erro: 'não é número' };
  }
  if (campo.tipo === 'opcoes') {
    const o = opcaoDeUnidade(bruto, campo.opcoes);
    return o ? { ok: true, valor: o, exibicao: o } : { ok: false, erro: `use ${campo.opcoes.join(', ')}` };
  }
  if (campo.tipo === 'obra' || campo.tipo.startsWith('lista:')) {
    const r = casarReferencia(bruto, itensDoCampo(campo, catalogo));
    return r.ok
      ? { ok: true, valor: r.item.id, exibicao: r.item.nome, aproximado: !r.exato }
      : { ok: false, erro: 'não identificado', sugestoes: r.sugestoes };
  }
  return { ok: true, valor: String(bruto).trim(), exibicao: String(bruto).trim() };
}
